import { useMutation } from '@tanstack/react-query';
import { Endpoint } from '@/constants';
import { postData } from '@/utils/postData';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { useCheckout } from '@/contexts/checkoutContext';
import type { ApplePayOrder, ApplePayOrderResponse } from '@/types/applePay';

const createApplePayOrder = (order: ApplePayOrder): Promise<ApplePayOrderResponse> =>
  postData(Endpoint.CreateApplePayOrder)(order as unknown as Record<string, unknown>);

interface UseApplePayOptions {
  onError?: () => void;
  isBuyNow?: boolean; // Add flag to indicate Buy Now mode
}

export const useApplePay = (options?: UseApplePayOptions) => {
  const { onError, isBuyNow } = options || {};

  const checkoutContext = !isBuyNow ? useCheckout() : null;
  const refreshCheckoutData = checkoutContext?.refreshCheckoutData;
  
  const { isPending, mutate } = useMutation<
    ApplePayOrderResponse,
    Error,
    ApplePayOrder
  >({
    mutationFn: createApplePayOrder,
    onError: error => {
      console.error(`Failed to submit Apple Pay payment: ${error.message}`);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Payment failed. Please try again or use a different payment method.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
      
      if (onError) {
        onError();
      }
    },
    onSuccess: async response => {
      console.log('Apple Pay: Order response received', response);
      
      // Handle sales order error
      if (response.salesOrderError) {
        const errorMessage = response.salesOrderError.message || 'Payment processing failed. Please try again.';
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {errorMessage}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        
        // If there's a redirect URL in the error, redirect after delay
        if (response.salesOrderError.redirectUrl) {
          setTimeout(() => {
            window.location.href = response.salesOrderError?.redirectUrl ?? '/checkout';
          }, 3000);
        }
        
        if (onError) {
          onError();
        }
        return;
      }
      
      // Handle successful order with nested data structure
      if (response.isSuccess && response.data?.redirectUrl) {
        console.log('Apple Pay: Order created successfully, redirecting to:', response.data.redirectUrl);
        
        // Refresh checkout data to clear the cart before redirecting
        if (!isBuyNow && refreshCheckoutData) {
          try {
            await refreshCheckoutData();
          } catch (error) {
            console.error('Apple Pay: Failed to refresh checkout data after order', error);
          }
        }
        
        // Redirect to confirmation page
        window.location.href = response.data.redirectUrl;
        return;
      }
      
      // Handle error message in data
      if (response.data?.errorMessage) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.data.errorMessage}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        
        if (onError) {
          onError();
        }
        return;
      }
      
      // Handle generic failure
      if (!response.isSuccess && response.messages && response.messages.length > 0) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.messages[0]}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        
        if (onError) {
          onError();
        }
        return;
      }
      
      // Unexpected response
      console.error('Apple Pay: Unexpected response format', response);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              An unexpected error occurred. Please try again.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
      
      if (onError) {
        onError();
      }
    },
  });

  return { isPending, createApplePayOrder: mutate };
};